import { getCurrentInstance, onBeforeUnmount, onUpdated } from "vue";

// component types not exported from @riverty/web-components
type EventName = "rClick" | "rChange" | "rInput" | "rFocus" | "rBlur";
type RElements = HTMLRInputElement | HTMLRSelectElement | HTMLRButtonElement;

// EventMaps not exported from @riverty/web-components
type RInputEventDetail = { value: string };
type RSelectEventDetail = { value: string };

type EventHandler<T extends RElements> = T extends HTMLRSelectElement ? (e: CustomEvent<RSelectEventDetail>) => void : (e: CustomEvent<RInputEventDetail>) => void;

export function useEventOverride<T extends RElements>(eventName: EventName, refName: string, handlerOrMethodName: EventHandler<T> | string, ...args: any[]): void {
    const instance = getCurrentInstance();
    let isAttached = false;

    // Helper to resolve the handler: either a direct function or a method on the instance
    const getHandler = (): EventListener => {
        if (typeof handlerOrMethodName === "function") {
            return handlerOrMethodName as EventListener;
        }

        return (e: Event) => {
            if (instance && instance.proxy) {
                const method = (instance.proxy as any)[handlerOrMethodName];
                if (typeof method === "function") {
                    method(e, ...args);
                }
            }
        };
    };

    const handler = getHandler();

    onUpdated(() => {
        // Resolve element dynamically from refs using the instance proxy
        const element = (instance?.proxy?.$refs[refName] as T) || null;

        if (!element && isAttached) {
            isAttached = false;
        }

        if (element && !isAttached) {
            element.addEventListener(eventName, handler);
            isAttached = true;
        }
    });

    onBeforeUnmount(() => {
        const element = (instance?.proxy?.$refs[refName] as T) || null;
        if (element && isAttached) {
            element.removeEventListener(eventName, handler);
            isAttached = false;
        }
    });
}
